<template>
	<div
		v-if="globalStore.organization && globalStore.fullName"
		class="app-settings relative"
		data-app-settings-layout
		:class="{
			'mobile': isMobile,
		}"
	>
		<CMobileNavHeader
			v-if="isMobile"
			:nav-items="isAccountSettings ? null : settingsNavigation"
			:context-label="isAccountSettings ? null : globalStore.organization.Name"
			:title="pageSubHeading"
			:header="currentRoute"
		/>
		<div class="flex min-h-0">
			<SettingsSidebar
				v-if="!isMobile"
				:page-heading="pageHeading"
				:page-sub-heading="pageSubHeading"
				:nav-items="settingsNavigation"
				class="shrink-0"
				@back="navigateBack"
			/>

			<div class="app-settings__content grow">
				<slot v-if="hasPermission" />
				<CHeaderLayout v-else @show-account-menu="$emit('show-account-menu', $event)">
					<CCallout type="warning" title="You do not have permission to view this page." class="w-fit" />
				</CHeaderLayout>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
	import { defineComponent, inject, watch, ref } from 'vue';
	import type { PropType } from 'vue';
	import type { GlobalStore } from 'src/stores/global-store';
	import SettingsSidebar from '../views/settings/components/SettingsSidebar.vue';
	import { checkRole } from 'src/views/admin/utils/role-check';
	import { getPreviewFeatures } from '@cognitoforms/api/services/feature-preview-service';
	import { getFallbackRoute, hasPreviousHistory } from 'src/views/router';
	import { cleanUrlPath } from '@cognitoforms/utils/url-helper';
	import CHeaderLayout from 'src/components/app/HeaderLayout.vue';
	import CCallout from 'src/components/content/Callout.vue';
	import { isMobileKey } from 'src/utilities/injection-keys';
	import CMobileNavHeader from 'src/components/navigation/MobileNavHeader.vue';

	export type NavigationItemType = 'header' | 'page';

	export type NavigationItem = {
		name: string;
		displayName: string;
		type: NavigationItemType;
	}

	export default defineComponent({
		components: {
			SettingsSidebar,
			CHeaderLayout,
			CCallout,
			CMobileNavHeader
		},
		inheritAttrs: false,
		props: {
			globalStore: { type: Object as PropType<GlobalStore>, required: true }
		},
		setup() {
			const isMobile = inject(isMobileKey);

			return {
				isMobile,
				hasPreviewFeatures: ref(false)
			};
		},
		head() {
			return {
				htmlAttrs: {
					class: [
						'not-iframe-page'
					]
				}
			};
		},
		computed: {
			isAccountSettings() : boolean {
				return this.$route.name === 'account-settings';
			},
			pageHeading() : string {
				return this.isAccountSettings
					? this.globalStore.fullName
					: this.globalStore.organization.Name;
			},
			pageSubHeading() : string {
				return this.isAccountSettings ? 'My Account' : 'Settings';
			},
			currentRoute() : string {
				return this.$route.meta.displayName;
			},
			hasPermission() : boolean {
				return this.isAccountSettings || checkRole(this.$route.meta.minimumRole);
			},
			settingsNavigation() : NavigationItem[] {
				if (this.$route.meta.sections) {
					return this.$route.meta.sections.map(function(section) {
						return {
							name: section.id,
							displayName: section.displayName,
							type: 'header'
						};
					});
				}
				else {
					const currentRoute = this.$router.getRoutes().find(r => r.name === this.$route.name);
					let parent = currentRoute.parent;

					if (currentRoute.path.split('/').length > 4)
						parent = parent.parent;

					// Find allowed sibling routes
					return this.$router.getRoutes()
						.filter(r => r.parent && r.parent === parent && r.meta.displayName)
						.filter(r => checkRole(r.meta.minimumRole))
						.filter(r => !r.name.endsWith('preview-features') || this.hasPreviewFeatures)
						.map(function(route) {
							return {
								name: route.name,
								displayName: route.meta.displayName,
								type: 'page'
							};
						});
				}
			},
			atSettingsRoot() {
				return this.$route.name === 'organization-settings';
			},
			isMobileMenuPage() {
				return this.isMobile && this.atSettingsRoot;
			}
		},
		created() {
			if (!this.isMobile && this.atSettingsRoot) {
				this.$router.replace(cleanUrlPath(this.$route.path + '/general'));
			}
		},
		mounted() {
			if (!this.isAccountSettings) {
				getPreviewFeatures().then((res) => {
					this.hasPreviewFeatures = !!res.Flags?.length;
				});
			}
		},
		methods: {
			navigateBack() {
				// If there are no other pages in history, go back to forms homepage
				if (!hasPreviousHistory())
					this.$router.push(getFallbackRoute(this.$router));
				else {
					this.$router.back();
					// Ensure non-mobile skips menu in the event settings was initially accessed via mobile
					if (!this.isMobile) {
						const unwatchRoute = watch(() => this.$route.fullPath, () => {
							unwatchRoute();
							if (this.$route.path.endsWith('/settings') || this.$route.path.endsWith('/myaccount'))
								this.navigateBack();
						});
					}
				}
			}
		}
	});
</script>

<style lang="scss">
@import '@cognitoforms/styles/app/common/common';

.app-settings {
	$sidebar-width-md: 220px;
	$sidebar-width-lg: 280px;

	// Fix for content getting cut off when PreviewBanner is present. No side effects from always having these styles.
	.admin--app-nav & {
		display: flex;
		flex-direction: column;
		min-height: 0;

		&__content {
			display: flex;
			flex-direction: column;
		}

		.header-layout {
			min-height: 0;
		}
	}

	.admin--mobile.admin--app-nav & .header-layout__header {
		padding-top: 0;
		padding-bottom: 0;

		h1 {
			display: none;
		}
	}

	h2 {
		font-size: $size__5;
	}

	.header-layout {
		@include b-up($b7) {
			--content-left-padding: #{$space__5};
		}

		@include b-up($b12) {
			--content-left-padding: #{$space__6};
		}

		&__content {
			width: 100%;

			main {
				max-width: calc(var(--content-max-width, 762px));
			}
		}
	}

	@include b-down($b5) {

		&__content {
			@include scrollbar($wide-scrollbar);
			min-height: 0;
		}
	}

	.button--secondary {

		.i-simple {
			vertical-align: bottom;
		}
	}

	// Responsive overrides, can be removed when our utilities are gone and Tailwind is left
	@include b-up($b10) {

		.b10\:gap-x-5 {
			column-gap: $space__5;
		}
	}

	@include b-up($b12) {

		.b12\:gap-x-6 {
			column-gap: $space__6;
		}
	}

	.admin--support-pinned & {

		&__content {
			margin-right: var(--support-width);
			transition: margin-right 0s ease-in-out .3s;
		}
	}
}
</style>